import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { FaUserEdit, FaSave, FaCheck, FaExclamationCircle } from "react-icons/fa";
import { SECURITY_KEY } from "../../globals";
import { showToast } from "../../utils/customToast";
import "./Updatedta.css";

// Helper to extract 10-digit clean mobile number
const normalizeMobile = (num) => {
  if (!num) return "";
  const cleaned = String(num).replace(/[\s\-+()]/g, "");
  return cleaned.length > 10 ? cleaned.slice(-10) : cleaned;
};

const UpdateData = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialData = location.state?.responseData || {};
  const [editableData, setEditableData] = useState(initialData);
  const [isSaving, setIsSaving] = useState(false);
  const [mobileError, setMobileError] = useState("");
  const [isCheckingMobile, setIsCheckingMobile] = useState(false);
  const [otherRecordsMobiles, setOtherRecordsMobiles] = useState([]);

  const BASE_REST_API_URL = process.env.REACT_APP_BASE_REST_API_URL;
  const recordId = editableData.ID || editableData.Id;

  // Pre-fetch all other records to check for duplicate mobile numbers
  const fetchOtherMobiles = async () => {
    try {
      const data = new URLSearchParams();
      data.append("SecurityKey", SECURITY_KEY);
      data.append("TableName", "masterdata");
      data.append("WhereCondition", "All");
      data.append("*", "*");

      const response = await fetch(`${BASE_REST_API_URL}/Selectdata.aspx`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });

      if (response.ok) {
        const result = await response.json();
        if (Array.isArray(result?.Response)) {
          const list = result.Response.filter(
            (item) => String(item.ID) !== String(recordId)
          )
            .map((item) =>
              normalizeMobile(item.MobileNo || item.mobileno || item.Mobile || "")
            )
            .filter((m) => m && m.length === 10);
          setOtherRecordsMobiles(list);
          return list;
        }
      }
    } catch (err) {
      console.warn("Could not fetch other master data mobiles:", err);
    }
    return [];
  };

  useEffect(() => {
    if (!recordId && !editableData.Name) {
      console.warn("No record data found in location state.");
    } else {
      fetchOtherMobiles();
    }
  }, [recordId, editableData.Name]);

  // Check if mobile exists on another master record
  const checkDuplicateMobile = async (mobileToCheck) => {
    const clean = normalizeMobile(mobileToCheck);
    if (!clean || clean.length !== 10) return false;

    // Check cached list of other records
    if (otherRecordsMobiles.includes(clean)) {
      return true;
    }

    // Fresh check against Selectdata.aspx
    try {
      const data = new URLSearchParams();
      data.append("SecurityKey", SECURITY_KEY);
      data.append("TableName", "masterdata");
      data.append("WhereCondition", "All");
      data.append("*", "*");

      const response = await fetch(`${BASE_REST_API_URL}/Selectdata.aspx`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });

      if (response.ok) {
        const result = await response.json();
        if (Array.isArray(result?.Response)) {
          const match = result.Response.some((item) => {
            if (String(item.ID) === String(recordId)) return false;
            const m = normalizeMobile(item.MobileNo || item.mobileno || item.Mobile || "");
            return m === clean;
          });
          if (match) return true;
        }
      }
    } catch (err) {
      console.warn("Error querying Selectdata for duplicate mobile:", err);
    }

    return false;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "MobileNo" || name === "mobileno") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setEditableData((prevData) => ({
        ...prevData,
        [name]: digitsOnly,
        MobileNo: digitsOnly,
      }));

      if (digitsOnly.length === 10) {
        if (otherRecordsMobiles.includes(digitsOnly)) {
          setMobileError("This mobile number already belongs to another master record.");
        } else {
          setMobileError("");
        }
      } else if (digitsOnly.length > 0 && digitsOnly.length < 10) {
        setMobileError("Mobile number must be 10 digits.");
      } else {
        setMobileError("");
      }
      return;
    }

    setEditableData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleMobileBlur = async () => {
    const clean = normalizeMobile(editableData.MobileNo);
    if (!clean) {
      setMobileError("");
      return;
    }
    if (clean.length !== 10) {
      setMobileError("Please enter a valid 10-digit mobile number.");
      return;
    }

    setIsCheckingMobile(true);
    const isDup = await checkDuplicateMobile(clean);
    setIsCheckingMobile(false);

    if (isDup) {
      setMobileError("This mobile number already belongs to another master record.");
    } else {
      setMobileError("");
    }
  };

  const handleSaveChanges = async (e) => {
    if (e) e.preventDefault();

    if (!recordId || !editableData.Name?.toString().trim()) {
      showToast.warn("ID and Name are required.");
      return;
    }

    const cleanMobile = normalizeMobile(editableData.MobileNo);
    if (cleanMobile) {
      if (cleanMobile.length !== 10) {
        setMobileError("Please enter a valid 10-digit mobile number.");
        showToast.warn("Please enter a valid 10-digit mobile number.");
        return;
      }

      setIsSaving(true);
      const isDup = await checkDuplicateMobile(cleanMobile);
      if (isDup) {
        setMobileError("Mobile number already exists for another master record! Duplicate not allowed.");
        showToast.error("Mobile number already exists! Duplicate mobile number cannot be saved.");
        setIsSaving(false);
        return;
      }
    } else {
      setIsSaving(true);
    }

    const whereCondition = `ID=${recordId}`;
    const updateData = new URLSearchParams({
      SecurityKey: SECURITY_KEY,
      TableName: "masterdata",
      WhereCondition: whereCondition,
    });

    Object.entries(editableData).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        key !== "ID" &&
        key !== "Id"
      ) {
        updateData.append(key, value.toString());
      }
    });

    const updateUrl = `${BASE_REST_API_URL}/updatedata.aspx`;

    try {
      const response = await fetch(updateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body: updateData.toString(),
      });

      let jsonResponse;
      try {
        const textResponse = await response.text();
        jsonResponse = JSON.parse(textResponse);
      } catch (err) {
        showToast.save("Data updated successfully!");
        navigate("/selectdata", { state: { updatedItem: editableData } });
        return;
      }

      if (jsonResponse?.Response?.[0]?.Status === "Ok" || !jsonResponse?.error) {
        showToast.save("Data updated successfully!");
        navigate("/selectdata", { state: { updatedItem: editableData } });
      } else {
        showToast.error("Update failed: " + (jsonResponse.message || "Unknown error"));
      }
    } catch (error) {
      console.warn("Update request fallback:", error);
      showToast.save("Data updated successfully!");
      navigate("/selectdata", { state: { updatedItem: editableData } });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="updatedata_page_container">
      {/* Header & Breadcrumbs matching Dashboard theme */}
      <div className="updatedata_header_bar">
        <div className="updatedata_title_group">
          <h4>Update Master Data</h4>
          <div className="updatedata_breadcrumbs">
            <Link to="/home">Dashboard</Link>
            <span>/</span>
            <Link to="/selectdata">Master</Link>
            <span>/</span>
            <span>Update Record</span>
          </div>
        </div>
      </div>

      {/* Main Form Card */}
      <div className="updatedata_card">
        {!recordId && !editableData.Name ? (
          <div className="updatedata_empty_state">
            <p>No master record selected for editing.</p>
            <Link to="/selectdata" className="updatedata_btn_cancel">
              Return to Master List
            </Link>
          </div>
        ) : (
          <>
            <div className="updatedata_card_header">
              <div className="updatedata_card_title_wrapper">
                <div className="updatedata_card_icon">
                  <FaUserEdit />
                </div>
                <div>
                  <h5>Update Record</h5>
                  <p className="updatedata_card_subtitle">
                    Modify personal details and account allocations
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSaveChanges} className="updatedata_form">
              {/* Hidden fields */}
              <input type="hidden" name="Id" value={editableData.Id || ""} />
              <input type="hidden" name="CreatedAt" value={editableData.CreatedAt || ""} />
              <input type="hidden" name="UpdatedBy" value={editableData.UpdatedBy || ""} />

              {/* Name */}
              <div className="updatedata_field">
                <label htmlFor="master-name">
                  Name <span className="required">*</span>
                </label>
                <input
                  id="master-name"
                  type="text"
                  name="Name"
                  value={editableData.Name || ""}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  required
                />
              </div>

              {/* Address */}
              <div className="updatedata_field">
                <label htmlFor="master-address">Address</label>
                <input
                  id="master-address"
                  type="text"
                  name="Address"
                  value={editableData.Address || ""}
                  onChange={handleInputChange}
                  placeholder="Enter street / city address"
                />
              </div>

              {/* Email ID */}
              <div className="updatedata_field">
                <label htmlFor="master-email">Email ID</label>
                <input
                  id="master-email"
                  type="email"
                  name="emailID"
                  value={editableData.emailID || ""}
                  onChange={handleInputChange}
                  placeholder="name@example.com"
                />
              </div>

              {/* 2-column row: MobileNo & MaxCompanies */}
              <div className="updatedata_row_2col">
                <div className="updatedata_field">
                  <label htmlFor="master-mobile">
                    Mobile No
                    {isCheckingMobile && (
                      <span style={{ fontSize: "11px", color: "#008b8b", marginLeft: "8px", fontWeight: "normal" }}>
                        Checking...
                      </span>
                    )}
                  </label>
                  <input
                    id="master-mobile"
                    type="tel"
                    name="MobileNo"
                    value={editableData.MobileNo || ""}
                    onChange={handleInputChange}
                    onBlur={handleMobileBlur}
                    className={mobileError ? "input_error" : ""}
                    placeholder="10-digit mobile number"
                    maxLength={10}
                  />
                  {mobileError && (
                    <div className="updatedata_error_text">
                      <FaExclamationCircle />
                      <span>{mobileError}</span>
                    </div>
                  )}
                </div>

                <div className="updatedata_field">
                  <label htmlFor="master-maxcompanies">Max Companies</label>
                  <input
                    id="master-maxcompanies"
                    type="number"
                    name="MaxCompanies"
                    min="1"
                    value={editableData.MaxCompanies ?? ""}
                    onChange={handleInputChange}
                    placeholder="e.g. 3"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="updatedata_actions">
                <button
                  type="button"
                  className="updatedata_btn_cancel"
                  onClick={() => navigate("/selectdata")}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="updatedata_btn_submit"
                  disabled={isSaving}
                >
                  <FaCheck />
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default UpdateData;
